import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldCheck, TrendingUp, Sparkles, Mail, CheckCircle2 } from 'lucide-react';

// Public investor page — mirrors the sanitized one-pager. Facts only, no secrets:
// the pitch is "it's real and working", proven on live rails 9 July 2026.
const PROOF_POINTS = [
  'Live marketplace — the full loop: discover → book → pay → service → rate',
  'Real payments on live Stripe rails — platform fee and stylist share split automatically at the moment of payment',
  'Trust & safety built in — government-ID + live-selfie verification for every stylist; no stylist takes a client until approved, verified and payout-ready',
  'A genuine 100% money-back guarantee — full refunds processed and returned same-day',
  'Legally grounded — Australian-registered business; AU Privacy Act 1988 & NZ Privacy Act 2020 compliant',
];

const VERSION_HISTORY = [
  ['v1.x', 'Go-live: marketplace loop, live Stripe payments, verified webhooks, legal pages'],
  ['v2.2–2.4', 'Google sign-in, admin console, transactional email, bot protection + lockout, security hardening'],
  ['v2.5–2.6', 'Stylist vetting: verified addresses, private credential vault, identity-verified badge'],
  ['v2.7', 'Verification gates — evidence before ID checks; payouts locked until verified'],
  ['v2.8', 'Guided onboarding: map-confirmed address, AU/NZ credential catalogue, bio builder'],
  ['v2.9', 'Discover photo carousels, reputation hearts, pending-vs-earned earnings'],
  ['v2.10', 'Complete booking lifecycle: slide-to-accept, proof-of-work completion, client rating + share moment'],
  ['v2.11', 'Cinematic landing, investor page, trust strip'],
];

export default function InvestorsPage() {
  return (
    <div className="min-h-screen py-16 px-6">
      <div className="container mx-auto max-w-4xl space-y-12">
        <div className="text-center space-y-4">
          <span className="text-xs font-montserrat font-semibold tracking-[0.3em] text-primary uppercase">For investors & partners</span>
          <h1 className="font-cormorant text-5xl lg:text-6xl font-bold bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
            Onda — luxury beauty, at your doorstep
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            A mobile-beauty marketplace where vetted, elite stylists travel to the client.
            Most early-stage pitches are a slide deck. <strong className="text-foreground">Onda is a live,
            secure, compliant product</strong> — built lean by a solo founder working with AI.
          </p>
        </div>

        <Card className="luxury-card">
          <CardContent className="p-8 space-y-4">
            <h2 className="font-cormorant text-3xl font-bold flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-primary" /> Proven on live rails — 9 July 2026
            </h2>
            <p className="text-sm text-muted-foreground">
              In a single verified end-to-end run on the live platform: a stylist completed the full vetting
              journey (credentials → business registration → government-ID + live-selfie verification →
              payment account activated), a client booked and paid <strong className="text-foreground">A$115 on live
              Stripe rails</strong> with the split landing to the cent, the job was completed with photographic
              proof of work, rated by the client — and a full refund was processed and returned the same day.
            </p>
            <ul className="space-y-2">
              {PROOF_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-primary font-medium">
              Translation: the build risk is largely retired. Capital goes to growth, not to finding out if it can be made.
            </p>
          </CardContent>
        </Card>

        <Card className="luxury-card">
          <CardContent className="p-8 space-y-4">
            <h2 className="font-cormorant text-3xl font-bold flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-primary" /> Business model
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div className="p-4 rounded-lg border border-border/40">
                <p className="font-semibold mb-1">Stylist subscription</p>
                <p className="text-muted-foreground">Onboarding fee + monthly platform membership — recurring revenue. Founding stylists onboard free to seed supply.</p>
              </div>
              <div className="p-4 rounded-lg border border-border/40">
                <p className="font-semibold mb-1">Booking commission</p>
                <p className="text-muted-foreground">A percentage of every booking, split automatically at payment — no invoicing, no chasing.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="luxury-card">
          <CardContent className="p-8 space-y-4">
            <h2 className="font-cormorant text-3xl font-bold flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-primary" /> Version history
            </h2>
            <p className="text-sm text-muted-foreground">
              Shipped as small, verified releases — every one live in production the same day. During active
              development: <strong className="text-foreground">28+ production releases in a single day</strong> — the
              velocity of the founder-plus-AI build model.
            </p>
            <div className="divide-y divide-border/30">
              {VERSION_HISTORY.map(([v, desc]) => (
                <div key={v} className="py-2.5 flex gap-4 text-sm">
                  <span className="font-mono text-primary w-20 shrink-0">{v}</span>
                  <span className="text-muted-foreground">{desc}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="text-center space-y-5 py-6">
          <h2 className="font-cormorant text-4xl font-bold">The product is built.</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            We're seeking support to accelerate stylist acquisition, client-side marketing in launch cities,
            and operations as the network scales. <strong className="text-foreground">This is fuel on a fire that's
            already lit.</strong>
          </p>
          <a href="mailto:onda@ivonharris.com?subject=Onda%20—%20investor%20enquiry">
            <Button className="btn-primary text-lg px-12 py-7">
              <Mail className="mr-2 h-5 w-5" /> Talk to the founder
            </Button>
          </a>
          <p className="text-xs text-muted-foreground">
            Onda is operated by ManaMetaMaori (ABN 27 281 805 464), Australia · <Link to="/" className="text-primary hover:underline">onda.ivonharris.com</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
